import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { jsonFetchRequest } from "@circle-react/helpers/backendRequestHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { withSearchDelay } from "@circle-react/hooks/utils/withSearchDelay";
import type { CommunityMember } from "@circle-react/types";
import { useAsyncSearch } from "@circleco/compass/hooks/useAsyncSearch";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import {
  buildCommunityMemberOptions,
  type CommunityMemberOption,
} from "./useCommunityMemberSelectOptions";

interface UseCommunityMemberSelectAsyncSearchParams {
  excludeAdmins?: boolean;
  filters?: Record<string, unknown>;
  getCommunityMemberText?: (member: CommunityMember) => string;
  includeInactive?: boolean;
  perPage?: number;
  valueField?: "id" | "user_id";
}

/**
 * @deprecated Use {@link useCommunityMemberCRMSearch} instead.
 * This hook will be removed once `crm_clickhouse_contacts_search_pickers_enabled`
 * is enabled for all communities.
 */
export const useCommunityMemberSelectAsyncSearch = ({
  excludeAdmins = false,
  filters,
  getCommunityMemberText,
  includeInactive = false,
  perPage = 10,
  valueField = "id",
}: UseCommunityMemberSelectAsyncSearchParams) => {
  const queryClient = useQueryClient();
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const [isOpen, setIsOpen] = useState(false);

  const filtersKey = useMemo(
    () => (filters ? JSON.stringify(filters) : null),
    [filters],
  );

  const excludedIds = useMemo(
    () =>
      new Set(
        (Array.isArray(filters?.exclude_ids) ? filters.exclude_ids : []).map(
          String,
        ),
      ),
    [filters],
  );

  const requestFilters = useMemo(
    () => ({
      exclude_admins: excludeAdmins,
      include_inactive: includeInactive,
      ...(filters ?? {}),
    }),
    [excludeAdmins, includeInactive, filters],
  );

  const search = useCallback(
    async (query: string, signal: AbortSignal) => {
      const records = await queryClient.fetchQuery({
        queryKey: [
          "community-members",
          "legacy-contacts-search",
          query,
          excludeAdmins,
          includeInactive,
          filtersKey,
          perPage,
        ],
        queryFn: async () => {
          const response = await jsonFetchRequest<{
            records: CommunityMember[];
          }>(
            internalApi.search.communityMembers.index({
              params: {
                query_type: "name_and_email",
                query,
                per_page: perPage,
              },
            }),
            {
              method: "POST",
              body: JSON.stringify({ filters: requestFilters }),
              signal,
            },
          );
          return response.records;
        },
        staleTime: 3 * 60 * 1000,
      });

      if (signal.aborted) return [];

      const visibleRecords = records.filter(
        member => !excludedIds.has(String(member[valueField] ?? member.id)),
      );

      return buildCommunityMemberOptions({
        communityMembers: visibleRecords,
        getCommunityMemberText,
        valueField,
        isAdmin,
      });
    },
    [
      queryClient,
      excludeAdmins,
      excludedIds,
      filtersKey,
      getCommunityMemberText,
      includeInactive,
      isAdmin,
      perPage,
      requestFilters,
      valueField,
    ],
  );

  const debouncedSearch = useMemo(() => withSearchDelay(search), [search]);

  const onSearch = useCallback(
    (query: string, signal: AbortSignal) =>
      query.trim() ? debouncedSearch(query, signal) : Promise.resolve([]),
    [debouncedSearch],
  );

  const { searchProps } = useAsyncSearch<CommunityMemberOption>({ onSearch });

  const handleInputValueChange: typeof searchProps.onInputValueChange = (
    query,
    details,
  ) => {
    if (
      ["input-change", "input-paste", "input-clear"].includes(details.reason)
    ) {
      setIsOpen(query.trim().length > 0);
    }
    searchProps.onInputValueChange(query, details);
  };

  return {
    isOpen,
    setIsOpen,
    searchProps: { ...searchProps, onInputValueChange: handleInputValueChange },
  };
};
